import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Paper,
  IconButton,
  Alert,
  Breadcrumbs,
  Link,
  Chip,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Fab,
  Divider,
  TablePagination,
  SelectChangeEvent,
  InputAdornment,
  RadioGroup,
  FormControlLabel,
  Radio,
  Grid,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import {
  Add,
  Edit,
  Delete,
  NavigateNext,
  Close,
  CheckCircle,
  Clear,
  DirectionsBoat,
  Speed,
  Anchor,
  SettingsSuggest,
  DesignServices,
  Tune,
  Visibility,
  Search,
} from "@mui/icons-material";
import moment from "moment";
import axios from "axios";

// Interfaces pour la modélisation des données
interface Prospect {
  id: string;
  firstName: string;
  lastName: string;
}

interface Employee {
  id: string;
  firstname: string;
  lastname: string;
}

interface Modele {
  id: string;
  modele: string;
  description: string;
  image?: string;
}

interface Engine {
  id: string;
  engine: string;
  engineDescription: string;
  fuelCapacity: string;
  maxSpeed: string;
  cruiseSpeed: string;
  mark?: string;
  propulsion?: "HB" | "IB";
  maxSpeedByModel: Record<string, string>;
}

interface Propeller {
  id: string;
  diameter: string;
  length: string;
  angle: string;
}

interface Option {
  id: string;
  option: string;
  description: string;
  imageUrl?: string;
  idModele?: string;
}

interface SpeedEntry {
  maxSpeed: string;
  cruiseSpeed: string;
  engine: { id: string };
  propeller: { id: string };
}

interface ModeleEngine {
  id?: string; // id peut être undefined pour les configurations locales
  modeleId: string;
  engineId: string;
  optionIds: string[];
  mark?: string;
  note?: string;
  propulsion?: "HB" | "IB";
  propellerId?: string;
  recommendedMaxSpeed?: string;
  recommendedCruiseSpeed?: string;
  clientDesiredMinMaxSpeed?: string;
  clientDesiredMaxMaxSpeed?: string;
  clientDesiredMinCruiseSpeed?: string;
  clientDesiredMaxCruiseSpeed?: string;
  // Champs supplémentaires pour l'affichage
  modeleName?: string;
  engineName?: string;
  optionNames?: string[];
  propellerDetails?: Propeller;
}

interface Wishlist {
  id: string;
  date: string;
  prospectId: string;
  employee: string;
  modeleEngineId?: string;
  prospect?: Prospect;
  employeeDetails?: Employee;
  configuration?: ModeleEngine; // Ajout de la configuration complète
}

// URL de base de votre API
const API_BASE_URL = process.env.REACT_APP_API_URL;
const IMAGE_URL = process.env.REACT_APP_Image;

const WishlistManagementPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // États pour les données
  const [wishlists, setWishlists] = useState<Wishlist[]>([]);
  const [prospects, setProspects] = useState<Prospect[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [models, setModels] = useState<Modele[]>([]);
  const [engines, setEngines] = useState<Engine[]>([]);
  const [engineMarks, setEngineMarks] = useState<string[]>([]);
  const [propellers, setPropellers] = useState<Propeller[]>([]);
  const [speedList, setSpeedList] = useState<SpeedEntry[]>([]);
  const [options, setOptions] = useState<Option[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filteredOptions, setFilteredOptions] = useState<Option[]>([]);
  const [isConfigurationDialogOpen, setIsConfigurationDialogOpen] =
    useState(false);
  const [selectedConfiguration, setSelectedConfiguration] =
    useState<ModeleEngine | null>(null);

  const handleViewConfiguration = (wishlist: Wishlist) => {
    setSelectedConfiguration(wishlist.configuration || null);
    setIsConfigurationDialogOpen(true);
  };

  // États pour les modales (dialogues)
  const [isFormDialogOpen, setIsFormDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isModeleEngineFormDialogOpen, setIsModeleEngineFormDialogOpen] =
    useState(false);
  const [isModelSelectDialogOpen, setIsModelSelectDialogOpen] = useState(false);
  const [selectedWishlist, setSelectedWishlist] = useState<Wishlist | null>(
    null
  );
  const [alert, setAlert] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  // État pour le formulaire
  const [formData, setFormData] = useState<Partial<Wishlist>>({
    date: new Date().toISOString(),
    prospectId: "",
    modeleEngineId: "",
  });

  const isFormValid = () => {
    return formData.prospectId;
  };

  // État pour le formulaire ModeleEngine
  const [modeleEngineFormData, setModeleEngineFormData] = useState<
    Partial<ModeleEngine>
  >({
    modeleId: "",
    engineId: "",
    optionIds: [],
    mark: "",
    note: "",
    propulsion: undefined,
    propellerId: "",
    recommendedMaxSpeed: "",
    recommendedCruiseSpeed: "",
    clientDesiredMinMaxSpeed: "",
    clientDesiredMaxMaxSpeed: "",
    clientDesiredMinCruiseSpeed: "",
    clientDesiredMaxCruiseSpeed: "",
  });

  const [otherEngineName, setOtherEngineName] = useState("");
  const [speedSource, setSpeedSource] = useState<"agent" | "client">("agent");

  // États pour le filtre et la pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedProspectFilter, setSelectedProspectFilter] = useState("");
  const [selectedEmployeeFilter, setSelectedEmployeeFilter] = useState("");
  const [modelSearchQuery, setModelSearchQuery] = useState("");

  // Fonction pour obtenir l'ID de l'utilisateur depuis le local storage
  const getUserIdFromLocalStorage = () => {
    return localStorage.getItem("userId");
  };

  // Chargement des données au montage du composant
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [
        wishlistsResponse,
        prospectsResponse,
        employeesResponse,
        modelsResponse,
        optionsResponse,
      ] = await Promise.all([
        axios.get<Wishlist[]>(`${API_BASE_URL}/wishlists`),
        axios.get<Prospect[]>(`${API_BASE_URL}/prospects`),
        axios.get<Employee[]>(`${API_BASE_URL}/access`),
        axios.get<Modele[]>(`${API_BASE_URL}/modeles`),
        axios.get<Option[]>(`${API_BASE_URL}/options-modele`),
      ]);

      const prospectsData = prospectsResponse.data;
      const employeesData = employeesResponse.data;

      // Associer les prospects et les employés à leurs wishlists respectifs pour la table
      const wishlistsWithDetails = wishlistsResponse.data.map((wishlist) => {
        const prospect = prospectsData.find(
          (p) => p.id === wishlist.prospect?.id
        );
        const employeeDetails = employeesData.find(
          (e) => e.id.toString() === wishlist.employee
        );
        return {
          ...wishlist,
          prospect: prospect,
          employeeDetails: employeeDetails,
        };
      });

      setWishlists(wishlistsWithDetails);
      setProspects(prospectsData);
      setEmployees(employeesData);
      setModels(
        modelsResponse.data.map((m) => ({
          ...m,
          imageUrl: `https://picsum.photos/seed/${m.id}/400/300`,
        }))
      );
      const optionsWithImages = optionsResponse.data.map((opt) => ({
        ...opt,
        imageUrl: `https://picsum.photos/seed/${opt.id}/80/80`,
      }));
      setOptions(optionsWithImages);
      setLoading(false);
    } catch (err: any) {
      setError("Error loading data. Please check the API server connection.");
      setLoading(false);
      console.error("Error fetching initial ", err);
    }
  };

  const fetchDataForModel = async (modeleId: string) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/modeles/${modeleId}/details`
      );
      const { engines, engineMarks, propellers, speedList } = response.data;
      setEngines(engines);
      setEngineMarks(engineMarks);
      setPropellers(propellers);
      setSpeedList(speedList);
    } catch (err: any) {
      setError(
        "Error fetching model details. Please check the API server connection."
      );
      console.error("Error fetching model details:", err);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleDateChange = (date: moment.Moment | null) => {
    if (date) {
      setFormData({ ...formData, date: date.toISOString() });
    }
  };

  const handleModelSelect = async (selectedModelId: string) => {
    // Réinitialiser les données liées au modèle
    setFormData({ ...formData, modeleEngineId: "" });
    setModeleEngineFormData({
      modeleId: selectedModelId,
      engineId: "",
      optionIds: [],
      propellerId: "",
      recommendedMaxSpeed: "",
      recommendedCruiseSpeed: "",
      clientDesiredMinMaxSpeed: "",
      clientDesiredMaxMaxSpeed: "",
      clientDesiredMinCruiseSpeed: "",
      clientDesiredMaxCruiseSpeed: "",
      mark: "",
      note: "",
      propulsion: undefined,
    });

    if (selectedModelId) {
      const optionsForModel = options.filter(
        (option) => option.idModele === selectedModelId
      );
      setFilteredOptions(optionsForModel);
      fetchDataForModel(selectedModelId);

      // Sinon, créer une configuration locale dans le frontend
      const localConfig = createLocalConfiguration(selectedModelId);
      setFormData({
        ...formData,
        configuration: localConfig,
        modeleEngineId: "local",
      });
      setAlert({
        type: "success",
        message:
          "Model selected. Configuration stored locally until prospect is chosen and wishlist is saved.",
      });
    } else {
      setFilteredOptions([]);
      // Si aucun modèle n'est sélectionné, effacer la configuration
      setFormData({
        ...formData,
        configuration: undefined,
        modeleEngineId: "",
      });
    }
    setIsModelSelectDialogOpen(false);
  };

  const handleEngineSelection = (event: SelectChangeEvent<string>) => {
    const selectedEngineId = event.target.value;
    setModeleEngineFormData((prevData) => ({
      ...prevData,
      engineId: selectedEngineId,
    }));
    if (selectedEngineId === "other") {
      setOtherEngineName("");
    } else {
      const selectedEngine = engines.find((e) => e.id === selectedEngineId);
      if (selectedEngine) {
        setModeleEngineFormData((prevData) => ({
          ...prevData,
          mark: selectedEngine.mark,
          propulsion: selectedEngine.propulsion,
        }));
      }
    }
  };

  const handleOptionSelection = (event: SelectChangeEvent<string[]>) => {
    setModeleEngineFormData((prevData) => ({
      ...prevData,
      optionIds: event.target.value as string[],
    }));
  };

  const handleRemoveOption = (optionId: string) => {
    setModeleEngineFormData((prevData) => ({
      ...prevData,
      optionIds: prevData.optionIds?.filter((id) => id !== optionId) || [],
    }));
  };

  const createLocalConfiguration = (selectedModelId: string): ModeleEngine => {
    const selectedModel = models.find((m) => m.id === selectedModelId);
    return {
      modeleId: selectedModelId,
      modeleName: selectedModel?.modele,
      engineId: "",
      optionIds: [],
      mark: "",
      note: "",
      propulsion: undefined,
      propellerId: "",
      recommendedMaxSpeed: "",
      recommendedCruiseSpeed: "",
      clientDesiredMinMaxSpeed: "",
      clientDesiredMaxMaxSpeed: "",
      clientDesiredMinCruiseSpeed: "",
      clientDesiredMaxCruiseSpeed: "",
      optionNames: [], // Initialisé comme tableau vide
      // propellerDetails n'est pas nécessaire pour la création locale
    };
  };
  const handleCreateModeleEngine = async () => {
    const selectedModel = models.find(
      (m) => m.id === modeleEngineFormData.modeleId
    );
    const selectedEngine = engines.find(
      (e) => e.id === modeleEngineFormData.engineId
    );
    const propellerId =
      modeleEngineFormData.propellerId === ""
        ? null
        : modeleEngineFormData.propellerId;
    const selectedPropeller = propellers.find((p) => p.id === propellerId);
    const selectedOptionNames = modeleEngineFormData.optionIds?.map(
      (id) => options.find((opt) => opt.id === id)?.option
    );

    // Créer un objet de configuration locale
    const localConfiguration = {
      ...modeleEngineFormData,
      modeleName: selectedModel?.modele,
      engineName:
        modeleEngineFormData.engineId === "other"
          ? otherEngineName
          : selectedEngine?.engine,
      optionNames: selectedOptionNames?.filter(
        (name): name is string => name !== undefined
      ),
      propellerDetails: propellerId ? selectedPropeller : undefined,
      // Ajouter les valeurs de vitesse selon la source
      recommendedMaxSpeed:
        speedSource === "agent"
          ? modeleEngineFormData.recommendedMaxSpeed
          : undefined,
      recommendedCruiseSpeed:
        speedSource === "agent"
          ? modeleEngineFormData.recommendedCruiseSpeed
          : undefined,
      clientDesiredMinMaxSpeed:
        speedSource === "client"
          ? modeleEngineFormData.clientDesiredMinMaxSpeed
          : undefined,
      clientDesiredMaxMaxSpeed:
        speedSource === "client"
          ? modeleEngineFormData.clientDesiredMaxMaxSpeed
          : undefined,
      clientDesiredMinCruiseSpeed:
        speedSource === "client"
          ? modeleEngineFormData.clientDesiredMinCruiseSpeed
          : undefined,
      clientDesiredMaxCruiseSpeed:
        speedSource === "client"
          ? modeleEngineFormData.clientDesiredMaxCruiseSpeed
          : undefined,
    };

    // Si on est en mode édition et qu'il y a déjà une configuration locale, la mettre à jour
    if (selectedWishlist && formData.configuration?.id) {
      try {
        // Mettre à jour la configuration existante
        const configResponse = await axios.put(
          `${API_BASE_URL}/configurations/${formData.configuration.id}`,
          localConfiguration
        );
        setFormData({ ...formData, configuration: configResponse.data });
        setAlert({
          type: "success",
          message: "Model configuration updated successfully!",
        });
      } catch (err: any) {
        setAlert({ type: "error", message: "Error updating configuration." });
        console.error("Error updating configuration:", err);
        return;
      }
    } else {
      // Stocker la configuration dans le formData (locale)
      setFormData({
        ...formData,
        configuration: localConfiguration as ModeleEngine,
        modeleEngineId: "local",
      });
      setAlert({
        type: "success",
        message: "Model configuration saved locally!",
      });
    }

    setIsModeleEngineFormDialogOpen(false);
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};
    if (!formData.prospectId) errors.prospectId = "Prospect is required.";
    if (!formData.modeleEngineId && !formData.configuration)
      errors.modeleEngineId = "Model configuration is required.";
    return Object.keys(errors).length === 0;
  };

  const handleCreateWishlist = async () => {
    const userId = getUserIdFromLocalStorage();
    if (!userId) {
      setAlert({
        type: "error",
        message: "User not authenticated. Please log in.",
      });
      return;
    }

    if (!validateForm()) {
      setAlert({
        type: "error",
        message: "Please fix the errors in the form.",
      });
      return;
    }

    try {
      let configurationId = formData.modeleEngineId;

      // Si la configuration est locale ou n'a pas encore d'ID backend, la créer/mettre à jour
      if (
        formData.configuration &&
        (formData.modeleEngineId === "local" || !formData.modeleEngineId)
      ) {
        const configPayload = {
          ...formData.configuration,
          prospectId: formData.prospectId,
        };
        // Si une configuration locale existe mais n'a pas encore été envoyée (pas d'ID)
        const configResponse = await axios.post(
          `${API_BASE_URL}/configurations`,
          configPayload
        );
        configurationId = configResponse.data.id;
      }
      // Si modeleEngineId est déjà un ID valide du backend, on l'utilise directement

      // Créer la wishlist avec l'ID de configuration
      const wishlistPayload = {
        date: formData.date,
        prospectId: formData.prospectId,
        employee: userId,
        modeleEngineId: configurationId, // Utiliser l'ID obtenu
      };

      await axios.post(`${API_BASE_URL}/wishlists`, wishlistPayload);
      setAlert({
        type: "success",
        message: "Wishlist created successfully! 🎉",
      });
      handleCloseFormDialog();
      fetchData();
    } catch (err: any) {
      setAlert({
        type: "error",
        message: err.response?.data?.message || "Error creating wishlist.",
      });
      console.error("Error creating wishlist:", err);
    }
  };

  const handleUpdateWishlist = async () => {
    if (!validateForm() || !selectedWishlist) {
      setAlert({
        type: "error",
        message: "Please fix the errors in the form.",
      });
      return;
    }

    try {
      let configurationId = formData.modeleEngineId;

      // Gérer la configuration : créer/mettre à jour si nécessaire
      if (formData.configuration) {
        const configPayload = {
          ...formData.configuration,
          prospectId: formData.prospectId,
        };

        if (formData.modeleEngineId && formData.modeleEngineId !== "local") {
          // Si une configuration existe déjà côté backend, la mettre à jour
          const configResponse = await axios.put(
            `${API_BASE_URL}/configurations/${formData.modeleEngineId}`,
            configPayload
          );
          configurationId = configResponse.data.id; // ID devrait normalement rester le même
        } else {
          // Si la configuration est locale ou n'a pas d'ID valide, la créer
          const configResponse = await axios.post(
            `${API_BASE_URL}/configurations`,
            configPayload
          );
          configurationId = configResponse.data.id;
        }
      } else if (
        formData.modeleEngineId &&
        formData.modeleEngineId !== "local"
      ) {
        // Si formData.configuration est null/undefined mais qu'on a un ID backend,
        // cela signifie qu'on ne modifie pas la configuration, on utilise l'ID existant
        configurationId = formData.modeleEngineId;
      } else {
        // Scénario improbable : pas de config locale et pas d'ID backend
        setAlert({
          type: "error",
          message: "No configuration available to save.",
        });
        return;
      }

      // Mettre à jour la wishlist avec l'ID de configuration
      const wishlistPayload = {
        date: formData.date,
        prospectId: formData.prospectId,
        employee: getUserIdFromLocalStorage(),
        modeleEngineId: configurationId, // Utiliser l'ID obtenu/mis à jour
      };

      await axios.put(
        `${API_BASE_URL}/wishlists/${selectedWishlist.id}`,
        wishlistPayload
      );
      setAlert({
        type: "success",
        message: "Wishlist updated successfully! ✨",
      });
      handleCloseFormDialog();
      fetchData();
    } catch (err: any) {
      setAlert({
        type: "error",
        message: err.response?.data?.message || "Error updating wishlist.",
      });
      console.error("Error updating wishlist:", err);
    }
  };

  // Modification de handleEditWishlist pour gérer le parsing correct de optionIds
  const handleEditWishlist = (wishlist: Wishlist) => {
    setSelectedWishlist(wishlist);
    setFormData({
      date: wishlist.date
        ? new Date(wishlist.date).toISOString().slice(0, 10)
        : new Date().toISOString().slice(0, 10),
      prospectId: wishlist.prospectId,
      modeleEngineId: wishlist.modeleEngineId,
      configuration: wishlist.configuration,
    });

    // S'assurer que optionIds est un tableau pour le Select multiple
    let optionIdsArray: string[] = [];
    const configOptionIds = wishlist.configuration?.optionIds;
    if (Array.isArray(configOptionIds)) {
      optionIdsArray = configOptionIds;
    } else if (typeof configOptionIds === "string") {
      try {
        const parsed = JSON.parse(configOptionIds);
        if (Array.isArray(parsed)) {
          optionIdsArray = parsed;
        }
      } catch (e) {
        console.error("Erreur lors du parsing de optionIds:", e);
      }
    }

    setModeleEngineFormData({
      modeleId: wishlist.configuration?.modeleId || "",
      engineId: wishlist.configuration?.engineId || "",
      optionIds: optionIdsArray, // Utiliser le tableau garanti ici
      mark: wishlist.configuration?.mark,
      note: wishlist.configuration?.note,
      propulsion: wishlist.configuration?.propulsion,
      propellerId: wishlist.configuration?.propellerId,
      recommendedMaxSpeed: wishlist.configuration?.recommendedMaxSpeed,
      recommendedCruiseSpeed: wishlist.configuration?.recommendedCruiseSpeed,
      clientDesiredMinMaxSpeed:
        wishlist.configuration?.clientDesiredMinMaxSpeed,
      clientDesiredMaxMaxSpeed:
        wishlist.configuration?.clientDesiredMaxMaxSpeed,
      clientDesiredMinCruiseSpeed:
        wishlist.configuration?.clientDesiredMinCruiseSpeed,
      clientDesiredMaxCruiseSpeed:
        wishlist.configuration?.clientDesiredMaxCruiseSpeed,
    });
    setIsFormDialogOpen(true);
  };

  const handleDeleteWishlist = async () => {
    if (!selectedWishlist) return;
    try {
      await axios.delete(`${API_BASE_URL}/wishlists/${selectedWishlist.id}`);
      setAlert({
        type: "success",
        message: "Wishlist deleted successfully. 🗑️",
      });
      setIsDeleteDialogOpen(false);
      setSelectedWishlist(null);
      fetchData();
    } catch (err: any) {
      setAlert({ type: "error", message: "Error deleting wishlist." });
      console.error("Error deleting wishlist:", err);
    }
  };

  const handleCloseFormDialog = () => {
    setIsFormDialogOpen(false);
    setSelectedWishlist(null);
    setFormData({
      date: new Date().toISOString(),
      prospectId: "",
      modeleEngineId: "",
      configuration: undefined,
    });
  };

  const handleCloseModeleEngineDialog = () => {
    setIsModeleEngineFormDialogOpen(false);
    setModeleEngineFormData({
      modeleId: "",
      engineId: "",
      optionIds: [],
      recommendedMaxSpeed: "",
      recommendedCruiseSpeed: "",
      clientDesiredMinMaxSpeed: "",
      clientDesiredMaxMaxSpeed: "",
      clientDesiredMinCruiseSpeed: "",
      clientDesiredMaxCruiseSpeed: "",
    });
    setOtherEngineName("");
    setSpeedSource("agent");
  };

  const filteredWishlists = wishlists.filter((wishlist) => {
    const prospectMatch = selectedProspectFilter
      ? wishlist.prospect?.id === selectedProspectFilter
      : true;
    const employeeMatch = selectedEmployeeFilter
      ? wishlist.employeeDetails?.id === selectedEmployeeFilter
      : true;
    return prospectMatch && employeeMatch;
  });

  const paginatedWishlists = filteredWishlists.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const findPredefinedSpeed = useMemo(() => {
    return speedList.find(
      (s) =>
        s.engine.id === modeleEngineFormData.engineId &&
        s.propeller.id === modeleEngineFormData.propellerId
    );
  }, [
    modeleEngineFormData.engineId,
    modeleEngineFormData.propellerId,
    speedList,
  ]);

  const selectedOptions = useMemo(() => {
    return options.filter(
      (option) => modeleEngineFormData.optionIds?.includes(option.id) || false
    );
  }, [modeleEngineFormData.optionIds, options]);

  const filteredModels = useMemo(() => {
    return models.filter(
      (model) =>
        (model.modele || "")
          .toLowerCase()
          .includes(modelSearchQuery.toLowerCase()) ||
        (model.description || "")
          .toLowerCase()
          .includes(modelSearchQuery.toLowerCase())
    );
  }, [models, modelSearchQuery]);

  if (loading) {
    return (
      <Container
        maxWidth="xl"
        sx={{
          py: 4,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "80vh",
        }}
      >
        <CircularProgress color="primary" sx={{ color: "#C4B08C" }} />
      </Container>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Container maxWidth="xl" sx={{ py: { xs: 2, md: 4 } }}>
        {alert && (
          <Alert
            severity={alert.type}
            sx={{ mb: 3, borderRadius: 2 }}
            onClose={() => setAlert(null)}
          >
            {alert.message}
          </Alert>
        )}
        <Breadcrumbs
          separator={<NavigateNext fontSize="small" />}
          sx={{ mb: 2 }}
        >
          <Link color="inherit" href="#" underline="hover">
            HOME
          </Link>
          <Typography color="text.primary">WISHLIST MANAGEMENT</Typography>
        </Breadcrumbs>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexDirection={{ xs: "column", md: "row" }}
          gap={2}
          mb={4}
        >
          <Typography variant="h4" fontWeight="bold" sx={{ color: "#333" }}>
            ✨ Wishlists
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setIsFormDialogOpen(true)}
            sx={{
              bgcolor: "#C4B08C",
              color: "#000",
              "&:hover": { bgcolor: "#b39d77" },
              px: 3,
              py: 1,
              borderRadius: 2,
              boxShadow: "0 4px 14px rgba(196, 176, 140, 0.4)",
            }}
          >
            New Wishlist
          </Button>
        </Box>
        <Paper
          sx={{
            p: { xs: 2, md: 3 },
            borderRadius: 3,
            boxShadow: "0 8px 32px rgba(0,0,0,0.08)",
            mb: 4,
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={3}
            flexDirection={{ xs: "column", sm: "row" }}
            gap={2}
          >
            <Typography variant="h6" fontWeight={600}>
              List of Wishlists
            </Typography>
            <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Filter by Prospect</InputLabel>
                <Select
                  value={selectedProspectFilter}
                  label="Filter by Prospect"
                  onChange={(e) => setSelectedProspectFilter(e.target.value)}
                >
                  <MenuItem value="">
                    <em>All Prospects</em>
                  </MenuItem>
                  {prospects.map((prospect) => (
                    <MenuItem key={prospect.id} value={prospect.id}>
                      {prospect.firstName} {prospect.lastName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Filter by Employee</InputLabel>
                <Select
                  value={selectedEmployeeFilter}
                  label="Filter by Employee"
                  onChange={(e) => setSelectedEmployeeFilter(e.target.value)}
                >
                  <MenuItem value="">
                    <em>All Employees</em>
                  </MenuItem>
                  {employees.map((employee) => (
                    <MenuItem key={employee.id} value={employee.id}>
                      {employee.firstname} {employee.lastname}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
          {isMobile ? (
            <Box>
              {paginatedWishlists.length > 0 ? (
                paginatedWishlists.map((wishlist) => (
                  <Paper
                    key={wishlist.id}
                    sx={{ p: 2, mb: 2, boxShadow: 1, borderRadius: 2 }}
                  >
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={1}
                    >
                      <Typography variant="body1" fontWeight="bold">
                        {wishlist.prospect?.firstName}{" "}
                        {wishlist.prospect?.lastName}
                      </Typography>
                      <Chip
                        label={moment(wishlist.date).format("YYYY-MM-DD")}
                        size="small"
                        sx={{
                          bgcolor: "#C4B08C",
                          color: "#000",
                          fontWeight: "bold",
                        }}
                      />
                      <Chip
                        label={wishlist.configuration?.modeleName || "No Model"}
                        size="small"
                        onClick={() => handleViewConfiguration(wishlist)}
                        sx={{
                          bgcolor: "#C4B08C",
                          color: "#000",
                          fontWeight: "bold",
                          cursor: "pointer",
                          "&:hover": { bgcolor: "#b39d77" },
                        }}
                      />
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      **Employee:** {wishlist.employeeDetails?.firstname}{" "}
                      {wishlist.employeeDetails?.lastname}
                    </Typography>
                    <Box
                      mt={2}
                      display="flex"
                      justifyContent="flex-end"
                      gap={1}
                    >
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => handleEditWishlist(wishlist)}
                        >
                          <Edit />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSelectedWishlist(wishlist);
                            setIsDeleteDialogOpen(true);
                          }}
                        >
                          <Delete color="error" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Paper>
                ))
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: "#f8f8f8" }}>
                        <TableCell>Date</TableCell>
                        <TableCell>Prospect</TableCell>
                        <TableCell>Employee</TableCell>
                        <TableCell>Configuration Model</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedWishlists.length > 0 ? (
                        paginatedWishlists.map((wishlist) => (
                          <TableRow key={wishlist.id} hover>
                            <TableCell>
                              {moment(wishlist.date).format("YYYY-MM-DD")}
                            </TableCell>
                            <TableCell>
                              {wishlist.prospect?.firstName}{" "}
                              {wishlist.prospect?.lastName}
                            </TableCell>
                            <TableCell>
                              {wishlist.employeeDetails?.firstname}{" "}
                              {wishlist.employeeDetails?.lastname}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={
                                  wishlist.configuration?.modeleName ||
                                  "No Model"
                                }
                                size="small"
                                onClick={() =>
                                  handleViewConfiguration(wishlist)
                                }
                                sx={{
                                  bgcolor: "#C4B08C",
                                  color: "#000",
                                  fontWeight: "bold",
                                  cursor: "pointer",
                                  "&:hover": { bgcolor: "#b39d77" },
                                }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Box
                                display="flex"
                                justifyContent="flex-end"
                                gap={1}
                              >
                                <Tooltip title="Edit">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleEditWishlist(wishlist)}
                                  >
                                    <Edit />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete">
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      setSelectedWishlist(wishlist);
                                      setIsDeleteDialogOpen(true);
                                    }}
                                  >
                                    <Delete color="error" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={9} align="center">
                            <Typography
                              variant="body1"
                              color="text.secondary"
                              p={2}
                            >
                              No wishlists found.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: "#f8f8f8" }}>
                    <TableCell>Date</TableCell>
                    <TableCell>Prospect</TableCell>
                    <TableCell>Employee</TableCell>
                    <TableCell>Configuration Model</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedWishlists.length > 0 ? (
                    paginatedWishlists.map((wishlist) => (
                      <TableRow key={wishlist.id} hover>
                        <TableCell>
                          {moment(wishlist.date).format("YYYY-MM-DD")}
                        </TableCell>
                        <TableCell>
                          {wishlist.prospect?.firstName}{" "}
                          {wishlist.prospect?.lastName}
                        </TableCell>
                        <TableCell>
                          {wishlist.employeeDetails?.firstname}{" "}
                          {wishlist.employeeDetails?.lastname}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={
                              wishlist.configuration?.modeleName || "No Model"
                            }
                            size="small"
                            onClick={() => handleViewConfiguration(wishlist)}
                            sx={{
                              bgcolor: "#C4B08C",
                              color: "#000",
                              fontWeight: "bold",
                              cursor: "pointer",
                              "&:hover": { bgcolor: "#b39d77" },
                            }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Box display="flex" justifyContent="flex-end" gap={1}>
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                onClick={() => handleEditWishlist(wishlist)}
                              >
                                <Edit />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setSelectedWishlist(wishlist);
                                  setIsDeleteDialogOpen(true);
                                }}
                              >
                                <Delete color="error" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={9} align="center">
                        <Typography
                          variant="body1"
                          color="text.secondary"
                          p={2}
                        >
                          No wishlists found.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredWishlists.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Wishlists per page"
          />
        </Paper>
        {/* Statistics */}
        <Box
          display="flex"
          flexDirection={{ xs: "column", md: "row" }}
          gap={3}
          mb={4}
        >
          <Paper
            sx={{
              p: 3,
              borderRadius: 3,
              boxShadow: "0 8px 32px rgba(0,0,0,0.08)",
              flex: 1,
              background: "linear-gradient(135deg, #C4B08C 0%, #d7c7a8 100%)",
              color: "white",
            }}
          >
            <Typography variant="h6" fontWeight={600} mb={1}>
              Total Wishlists
            </Typography>
            <Typography variant="h3" fontWeight={700}>
              {wishlists.length}
            </Typography>
            <Typography variant="body2">Wishlists created</Typography>
          </Paper>
          <Paper
            sx={{
              p: 3,
              borderRadius: 3,
              boxShadow: "0 8px 32px rgba(0,0,0,0.08)",
              flex: 1,
              background: "linear-gradient(135deg, #90caf9 0%, #64b5f6 100%)",
              color: "white",
            }}
          >
            <Typography variant="h6" fontWeight={600} mb={1}>
              Total Prospects
            </Typography>
            <Typography variant="h3" fontWeight={700}>
              {prospects.length}
            </Typography>
            <Typography variant="body2">Registered prospects</Typography>
          </Paper>
          <Paper
            sx={{
              p: 3,
              borderRadius: 3,
              boxShadow: "0 8px 32px rgba(0,0,0,0.08)",
              flex: 1,
              background: "linear-gradient(135deg, #a5d6a7 0%, #81c784 100%)",
              color: "white",
            }}
          >
            <Typography variant="h6" fontWeight={600} mb={1}>
              Total Models
            </Typography>
            <Typography variant="h3" fontWeight={700}>
              {models.length}
            </Typography>
            <Typography variant="body2">Available boat models</Typography>
          </Paper>
        </Box>
        <Dialog
          open={isFormDialogOpen}
          onClose={(event, reason) => {
            // Only close when the X button is clicked
            if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
              handleCloseFormDialog();
            }
          }}
          fullWidth
          maxWidth="sm"
          PaperProps={{ sx: { borderRadius: 2 } }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              bgcolor: "#f8f8f8",
              borderBottom: "1px solid #eaeaea",
            }}
          >
            <Typography variant="h6" fontWeight="600">
              {selectedWishlist ? "Edit Wishlist" : "Create New Wishlist"}
            </Typography>
            <IconButton onClick={handleCloseFormDialog} size="small">
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DatePicker
                label="Date"
                value={moment(formData.date)}
                onChange={handleDateChange}
                slotProps={{
                  textField: { fullWidth: true, sx: { mb: 2, mt: 2 } },
                }}
              />
            </LocalizationProvider>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="prospect-select-label">Prospect</InputLabel>
              <Select
                labelId="prospect-select-label"
                value={formData.prospectId}
                label="Prospect"
                onChange={(e) =>
                  setFormData({ ...formData, prospectId: e.target.value })
                }
              >
                {prospects.map((prospect) => (
                  <MenuItem key={prospect.id} value={prospect.id}>
                    {prospect.firstName} {prospect.lastName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ mb: 2 }}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => setIsModelSelectDialogOpen(true)}
                startIcon={<DirectionsBoat />}
                sx={{
                  py: 1.5,
                  borderColor: "#C4B08C",
                  color: "#C4B08C",
                  "&:hover": {
                    borderColor: "#b39d77",
                    backgroundColor: "rgba(196, 176, 140, 0.1)",
                  },
                }}
              >
                Select Model
              </Button>
              {modeleEngineFormData.modeleId && (
                <Box mt={2} display="flex" alignItems="center" gap={1}>
                  <CheckCircle color="success" />
                  <Typography variant="body2" color="text.secondary">
                    Model selected:{" "}
                    <strong>
                      {
                        models.find(
                          (m) => m.id === modeleEngineFormData.modeleId
                        )?.modele
                      }
                    </strong>
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() =>
                      setModeleEngineFormData({
                        ...modeleEngineFormData,
                        modeleId: "",
                      })
                    }
                  >
                    <Clear fontSize="small" />
                  </IconButton>
                </Box>
              )}
            </Box>
            {modeleEngineFormData.modeleId && (
              <Button
                fullWidth
                variant="contained"
                onClick={() => setIsModeleEngineFormDialogOpen(true)}
                sx={{
                  mb: 2,
                  bgcolor: "#C4B08C",
                  color: "#000",
                  "&:hover": { bgcolor: "#b39d77" },
                }}
                disabled={!formData.prospectId}
              >
                Configure Model Engines and Options
              </Button>
            )}

            <TextField
              fullWidth
              label="Employee"
              name="employee"
              value={
                employees.find(
                  (emp) => emp.id.toString() === getUserIdFromLocalStorage()
                )?.firstname || ""
              }
              disabled
              sx={{ mb: 2 }}
            />
          </DialogContent>
          <DialogActions sx={{ p: 3, borderTop: "1px solid #eaeaea" }}>
            <Button onClick={handleCloseFormDialog}>Cancel</Button>
            <Button
              variant="contained"
              onClick={
                selectedWishlist ? handleUpdateWishlist : handleCreateWishlist
              }
              disabled={!isFormValid()}
              sx={{
                bgcolor: isFormValid() ? "#C4B08C" : "rgba(0, 0, 0, 0.12)",
                color: isFormValid() ? "#000" : "rgba(0, 0, 0, 0.26)",
                "&.Mui-disabled": {
                  bgcolor: "rgba(0, 0, 0, 0.12)",
                  color: "rgba(0, 0, 0, 0.26)",
                },
                "&:hover": {
                  bgcolor: isFormValid() ? "#b39d77" : "rgba(0, 0, 0, 0.12)",
                },
              }}
            >
              {selectedWishlist ? "Update" : "Create"}
            </Button>
          </DialogActions>
        </Dialog>
        {/* New Model Selection Dialog */}
        <Dialog
          open={isModelSelectDialogOpen}
          onClose={(event, reason) => {
            // Only close when the X button is clicked
            if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
              setIsModelSelectDialogOpen(false);
            }
          }}
          fullWidth
          maxWidth="lg"
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              bgcolor: "#C4B08C",
              color: "#000",
              borderBottom: "1px solid #eaeaea",
              p: 2,
            }}
          >
            <Typography variant="h6" fontWeight="bold">
              Select a Model
            </Typography>
            <IconButton
              onClick={() => setIsModelSelectDialogOpen(false)}
              size="small"
              sx={{ color: "#000" }}
            >
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 4, bgcolor: "#f5f5f5" }}>
            <TextField
              fullWidth
              label="Search Models"
              variant="outlined"
              value={modelSearchQuery}
              onChange={(e) => setModelSearchQuery(e.target.value)}
              sx={{ mb: 4, mt: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                flexWrap: "wrap",
                gap: 3,
                justifyContent: { xs: "center", sm: "flex-start" },
              }}
            >
              {filteredModels.map((model) => (
                <Box
                  key={model.id}
                  sx={{
                    flex: "1 1 300px",
                    maxWidth: {
                      xs: "100%",
                      sm: "calc(50% - 12px)",
                      md: "calc(33.333% - 16px)",
                    },
                  }}
                >
                  <Paper
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                      cursor: "pointer",
                      transition: "transform 0.2s, box-shadow 0.2s",
                      "&:hover": {
                        transform: "translateY(-5px)",
                        boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
                      },
                    }}
                    onClick={() => handleModelSelect(model.id)}
                  >
                    <Box
                      sx={{
                        position: "relative",
                        height: 180,
                        borderRadius: 2,
                        overflow: "hidden",
                        mb: 2,
                      }}
                    >
                      <img
                        src={IMAGE_URL + "/modeles/" + model.image}
                        alt={model.modele}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    </Box>
                    <Typography
                      variant="h6"
                      fontWeight="bold"
                      sx={{ color: "#333" }}
                    >
                      {model.modele}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 1 }}
                    >
                      {model.description}
                    </Typography>
                  </Paper>
                </Box>
              ))}
              {filteredModels.length === 0 && (
                <Box sx={{ width: "100%" }}>
                  <Typography align="center" color="text.secondary">
                    No models found.
                  </Typography>
                </Box>
              )}
            </Box>
          </DialogContent>
        </Dialog>
        <Dialog
          open={isModeleEngineFormDialogOpen}
          onClose={(event, reason) => {
            // Only close when the X button is clicked
            if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
              handleCloseModeleEngineDialog();
            }
          }}
          fullWidth
          maxWidth="lg"
          PaperProps={{ sx: { borderRadius: 2 } }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              bgcolor: "#C4B08C",
              color: "#000",
              borderBottom: "1px solid #eaeaea",
              p: 2,
            }}
          >
            <Typography variant="h6" fontWeight="bold">
              <SettingsSuggest sx={{ mr: 1 }} /> Configure Model:{" "}
              {
                models.find((m) => m.id === modeleEngineFormData.modeleId)
                  ?.modele
              }
            </Typography>
            <IconButton
              onClick={handleCloseModeleEngineDialog}
              size="small"
              sx={{ color: "#000" }}
            >
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 4, bgcolor: "#f5f5f5" }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                gap: 4,
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  p: 3,
                  bgcolor: "white",
                  borderRadius: 2,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                }}
              >
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  mb={2}
                  color="primary"
                >
                  <DirectionsBoat sx={{ mr: 1, verticalAlign: "middle" }} />{" "}
                  Engine & Propeller Selection
                </Typography>
                <Divider sx={{ mb: 3 }} />
                <FormControl fullWidth sx={{ mb: 3 }}>
                  <InputLabel id="engine-select-label">Engine</InputLabel>
                  <Select
                    labelId="engine-select-label"
                    value={modeleEngineFormData.engineId}
                    label="Engine"
                    onChange={handleEngineSelection}
                  >
                    <MenuItem value="">
                      <em>Select an Engine</em>
                    </MenuItem>
                    {engines.map((engine) => (
                      <MenuItem key={engine.id} value={engine.id}>
                        {engine.engine}
                      </MenuItem>
                    ))}
                    <MenuItem value="other">
                      <em>Other...</em>
                    </MenuItem>
                  </Select>
                </FormControl>
                {modeleEngineFormData.engineId === "other" && (
                  <TextField
                    fullWidth
                    label="Other Engine Name"
                    value={otherEngineName}
                    onChange={(e) => setOtherEngineName(e.target.value)}
                    sx={{ mb: 3 }}
                  />
                )}
                <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
                  <FormControl fullWidth>
                    <InputLabel id="mark-select-label">Mark</InputLabel>
                    <Select
                      labelId="mark-select-label"
                      value={modeleEngineFormData.mark || ""}
                      label="Brand"
                      onChange={(e) =>
                        setModeleEngineFormData({
                          ...modeleEngineFormData,
                          mark: e.target.value,
                        })
                      }
                    >
                      <MenuItem value="">
                        <em>Select a Mark</em>
                      </MenuItem>
                      {engineMarks.map((mark) => (
                        <MenuItem key={mark} value={mark}>
                          {mark}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl fullWidth>
                    <InputLabel id="propulsion-select-label">
                      Propulsion
                    </InputLabel>
                    <Select
                      labelId="propulsion-select-label"
                      value={modeleEngineFormData.propulsion || ""}
                      label="Propulsion"
                      onChange={(e) =>
                        setModeleEngineFormData({
                          ...modeleEngineFormData,
                          propulsion: e.target.value as "HB" | "IB",
                        })
                      }
                    >
                      <MenuItem value="">
                        <em>Select Propulsion</em>
                      </MenuItem>
                      <MenuItem value="HB">HB</MenuItem>
                      <MenuItem value="IB">IB</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <FormControl fullWidth sx={{ mb: 3 }}>
                  <InputLabel id="propeller-select-label">Propeller</InputLabel>
                  <Select
                    labelId="propeller-select-label"
                    value={modeleEngineFormData.propellerId || ""}
                    label="Propeller"
                    onChange={(e) =>
                      setModeleEngineFormData({
                        ...modeleEngineFormData,
                        propellerId: e.target.value,
                      })
                    }
                  >
                    <MenuItem value="">
                      <em>Select a Propeller</em>
                    </MenuItem>
                    {propellers.map((propeller) => (
                      <MenuItem key={propeller.id} value={propeller.id}>
                        {propeller.diameter} - {propeller.length}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  mb={2}
                  color="primary"
                >
                  <Speed sx={{ mr: 1, verticalAlign: "middle" }} /> Speed
                  Configuration
                </Typography>
                <Divider sx={{ mb: 3 }} />
                {findPredefinedSpeed ? (
                  <Box>
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Speeds for this configuration are pre-defined by the
                      manufacturer.
                    </Alert>
                    <TextField
                      fullWidth
                      label="Max Speed"
                      value={findPredefinedSpeed.maxSpeed}
                      disabled
                      sx={{ mb: 2 }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">km/h</InputAdornment>
                        ),
                      }}
                    />
                    <TextField
                      fullWidth
                      label="Cruise Speed"
                      value={findPredefinedSpeed.cruiseSpeed}
                      disabled
                      sx={{ mb: 2 }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">km/h</InputAdornment>
                        ),
                      }}
                    />
                  </Box>
                ) : (
                  <Box>
                    <FormControl component="fieldset" sx={{ mb: 2 }}>
                      <RadioGroup
                        row
                        value={speedSource}
                        onChange={(e) =>
                          setSpeedSource(e.target.value as "agent" | "client")
                        }
                      >
                        <FormControlLabel
                          value="agent"
                          control={<Radio />}
                          label={
                            <>
                              <DesignServices
                                sx={{ verticalAlign: "middle", mr: 0.5 }}
                              />{" "}
                              Agent Recommended
                            </>
                          }
                        />
                        <FormControlLabel
                          value="client"
                          control={<Radio />}
                          label={
                            <>
                              <Tune sx={{ verticalAlign: "middle", mr: 0.5 }} />{" "}
                              Client Desired
                            </>
                          }
                        />
                      </RadioGroup>
                    </FormControl>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      Speed values are not available. Please recommend values
                      for the client.
                    </Alert>
                    {speedSource === "agent" ? (
                      <>
                        <TextField
                          fullWidth
                          label="Recommended Max Speed"
                          name="recommendedMaxSpeed"
                          value={modeleEngineFormData.recommendedMaxSpeed}
                          onChange={(e) =>
                            setModeleEngineFormData({
                              ...modeleEngineFormData,
                              recommendedMaxSpeed: e.target.value,
                            })
                          }
                          sx={{ mb: 2 }}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                km/h
                              </InputAdornment>
                            ),
                          }}
                        />
                        <TextField
                          fullWidth
                          label="Recommended Cruise Speed"
                          name="recommendedCruiseSpeed"
                          value={modeleEngineFormData.recommendedCruiseSpeed}
                          onChange={(e) =>
                            setModeleEngineFormData({
                              ...modeleEngineFormData,
                              recommendedCruiseSpeed: e.target.value,
                            })
                          }
                          sx={{ mb: 2 }}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                km/h
                              </InputAdornment>
                            ),
                          }}
                        />
                      </>
                    ) : (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 2,
                          p: 2,
                          border: "1px solid #ccc",
                          borderRadius: 2,
                        }}
                      >
                        <Typography variant="body1" fontWeight="bold">
                          Max Speed Interval
                        </Typography>
                        <Box sx={{ display: "flex", gap: 2 }}>
                          <TextField
                            fullWidth
                            label="Min Max Speed"
                            name="clientDesiredMinMaxSpeed"
                            type="number"
                            value={
                              modeleEngineFormData.clientDesiredMinMaxSpeed
                            }
                            onChange={(e) =>
                              setModeleEngineFormData({
                                ...modeleEngineFormData,
                                clientDesiredMinMaxSpeed: e.target.value,
                              })
                            }
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  km/h
                                </InputAdornment>
                              ),
                            }}
                          />
                          <TextField
                            fullWidth
                            label="Max Max Speed"
                            name="clientDesiredMaxMaxSpeed"
                            type="number"
                            value={
                              modeleEngineFormData.clientDesiredMaxMaxSpeed
                            }
                            onChange={(e) =>
                              setModeleEngineFormData({
                                ...modeleEngineFormData,
                                clientDesiredMaxMaxSpeed: e.target.value,
                              })
                            }
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  km/h
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Box>
                        <Divider />
                        <Typography variant="body1" fontWeight="bold">
                          Cruise Speed Interval
                        </Typography>
                        <Box sx={{ display: "flex", gap: 2 }}>
                          <TextField
                            fullWidth
                            label="Min Cruise Speed"
                            name="clientDesiredMinCruiseSpeed"
                            type="number"
                            value={
                              modeleEngineFormData.clientDesiredMinCruiseSpeed
                            }
                            onChange={(e) =>
                              setModeleEngineFormData({
                                ...modeleEngineFormData,
                                clientDesiredMinCruiseSpeed: e.target.value,
                              })
                            }
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  km/h
                                </InputAdornment>
                              ),
                            }}
                          />
                          <TextField
                            fullWidth
                            label="Max Cruise Speed"
                            name="clientDesiredMaxCruiseSpeed"
                            type="number"
                            value={
                              modeleEngineFormData.clientDesiredMaxCruiseSpeed
                            }
                            onChange={(e) =>
                              setModeleEngineFormData({
                                ...modeleEngineFormData,
                                clientDesiredMaxCruiseSpeed: e.target.value,
                              })
                            }
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  km/h
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Box>
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
              <Box
                sx={{
                  flex: 1,
                  p: 3,
                  bgcolor: "white",
                  borderRadius: 2,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                }}
              >
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  mb={2}
                  color="primary"
                >
                  <Anchor sx={{ mr: 1, verticalAlign: "middle" }} /> Select
                  Options
                </Typography>
                <Divider sx={{ mb: 3 }} />
                <FormControl fullWidth sx={{ mb: 3 }}>
                  <InputLabel id="options-select-label">Options</InputLabel>
                  <Select
                    labelId="options-select-label"
                    multiple
                    value={modeleEngineFormData.optionIds}
                    onChange={handleOptionSelection}
                    label="Options"
                    renderValue={(selected) => (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {selected.map((value) => {
                          const option = options.find(
                            (opt) => opt.id === value
                          );
                          if (!option) return null;
                          return (
                            <Chip
                              key={value}
                              label={option.option}
                              avatar={
                                <img
                                  src={option.imageUrl}
                                  alt=""
                                  style={{
                                    width: 24,
                                    height: 24,
                                    borderRadius: "50%",
                                  }}
                                />
                              }
                              sx={{
                                "& .MuiChip-avatar": {
                                  marginRight: -1.5,
                                  marginLeft: 0.5,
                                },
                              }}
                            />
                          );
                        })}
                      </Box>
                    )}
                  >
                    {filteredOptions.map((option) => (
                      <MenuItem
                        key={option.id}
                        value={option.id}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          "&.Mui-selected": {
                            backgroundColor: "rgba(196, 176, 140, 0.1)",
                          },
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <img
                            src={option.imageUrl}
                            alt={option.option}
                            style={{
                              width: 40,
                              height: 40,
                              marginRight: 16,
                              borderRadius: "4px",
                            }}
                          />
                          <Box>
                            <Typography>{option.option}</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {option.description}
                            </Typography>
                          </Box>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {selectedOptions.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle1" fontWeight="bold" mb={1}>
                      Selected Options:
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
                      {selectedOptions.map((option) => (
                        <Paper
                          key={option.id}
                          sx={{
                            p: 3,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 1.5,
                            borderRadius: 2,
                            boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                            minWidth: 200,
                            position: "relative",
                          }}
                        >
                          <IconButton
                            onClick={() => handleRemoveOption(option.id)}
                            size="small"
                            sx={{ position: "absolute", top: 8, right: 8 }}
                          >
                            <Close fontSize="small" />
                          </IconButton>
                          <img
                            src={option.imageUrl}
                            alt={option.option}
                            style={{
                              width: 80,
                              height: 80,
                              borderRadius: "8px",
                            }}
                          />
                          <Box sx={{ textAlign: "center" }}>
                            <Typography variant="body1" fontWeight="bold">
                              {option.option}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {option.description}
                            </Typography>
                          </Box>
                        </Paper>
                      ))}
                    </Box>
                  </Box>
                )}
                <TextField
                  fullWidth
                  label="Notes"
                  name="notes"
                  multiline
                  rows={4}
                  value={modeleEngineFormData.note}
                  onChange={(e) =>
                    setModeleEngineFormData({
                      ...modeleEngineFormData,
                      note: e.target.value,
                    })
                  }
                  sx={{ mt: 3 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start"></InputAdornment>
                    ),
                  }}
                />
              </Box>
            </Box>
          </DialogContent>
          <DialogActions
            sx={{ p: 3, borderTop: "1px solid #eaeaea", bgcolor: "#f5f5f5" }}
          >
            <Button
              onClick={handleCloseModeleEngineDialog}
              sx={{ color: "#000" }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleCreateModeleEngine}
              sx={{
                bgcolor: "#C4B08C",
                color: "#000",
                "&:hover": { bgcolor: "#b39d77" },
              }}
            >
              {selectedWishlist && formData.configuration?.id
                ? "Update Configuration"
                : "Save Configuration"}
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog
          open={isDeleteDialogOpen}
          onClose={(event, reason) => {
            // Only close when the X button is clicked
            if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
              setIsDeleteDialogOpen(false);
            }
          }}
          maxWidth="xs"
          fullWidth
          PaperProps={{ sx: { borderRadius: 2 } }}
        >
          <DialogTitle sx={{ fontWeight: 600 }}>Confirm Deletion</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete the wishlist for{" "}
              <Typography component="span" fontWeight="bold">
                {selectedWishlist?.prospect?.firstName}{" "}
                {selectedWishlist?.prospect?.lastName}
              </Typography>
              ?
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={1}>
              This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button onClick={() => setIsDeleteDialogOpen(false)}>Cancel</Button>
            <Button
              color="error"
              variant="contained"
              onClick={handleDeleteWishlist}
              startIcon={<Delete />}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog
          open={isConfigurationDialogOpen}
          onClose={(event, reason) => {
            // Only close when the X button is clicked
            if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
              setIsConfigurationDialogOpen(false);
            }
          }}
          fullWidth
          maxWidth="md"
          PaperProps={{ sx: { borderRadius: 2 } }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              bgcolor: "#f8f8f8",
              borderBottom: "1px solid #eaeaea",
            }}
          >
            <Typography variant="h6" fontWeight="600">
              Configuration Details
            </Typography>
            <IconButton
              onClick={() => setIsConfigurationDialogOpen(false)}
              size="small"
            >
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            {selectedConfiguration ? (
              <Box>
                <Typography variant="subtitle1" fontWeight="bold">
                  Model:
                </Typography>
                <Typography variant="body1">
                  {selectedConfiguration.modeleName}
                </Typography>
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  sx={{ mt: 2 }}
                >
                  Engine:
                </Typography>
                <Typography variant="body1">
                  {selectedConfiguration.engineName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Mark: {selectedConfiguration.mark}, Propulsion:{" "}
                  {selectedConfiguration.propulsion}
                </Typography>
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  sx={{ mt: 2 }}
                >
                  Propeller:
                </Typography>
                {selectedConfiguration.propellerDetails ? (
                  <Typography variant="body1">
                    Diameter: {selectedConfiguration.propellerDetails.diameter},
                    Length: {selectedConfiguration.propellerDetails.length}
                  </Typography>
                ) : (
                  <Typography variant="body1" color="text.secondary">
                    No propeller selected.
                  </Typography>
                )}
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  sx={{ mt: 2 }}
                >
                  Speeds:
                </Typography>
                {selectedConfiguration.recommendedMaxSpeed ? (
                  <Box>
                    <Typography variant="body1">
                      Recommended Max Speed:{" "}
                      {selectedConfiguration.recommendedMaxSpeed} km/h
                    </Typography>
                    <Typography variant="body1">
                      Recommended Cruise Speed:{" "}
                      {selectedConfiguration.recommendedCruiseSpeed} km/h
                    </Typography>
                  </Box>
                ) : selectedConfiguration.clientDesiredMinMaxSpeed ? (
                  <Box>
                    <Typography variant="body1">
                      Client Desired Max Speed:{" "}
                      {selectedConfiguration.clientDesiredMinMaxSpeed} -{" "}
                      {selectedConfiguration.clientDesiredMaxMaxSpeed} km/h
                    </Typography>
                    <Typography variant="body1">
                      Client Desired Cruise Speed:{" "}
                      {selectedConfiguration.clientDesiredMinCruiseSpeed} -{" "}
                      {selectedConfiguration.clientDesiredMaxCruiseSpeed} km/h
                    </Typography>
                  </Box>
                ) : (
                  <Typography variant="body1" color="text.secondary">
                    No speed information available.
                  </Typography>
                )}
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold">
                    Notes:
                  </Typography>
                  <Typography variant="body1">
                    {selectedConfiguration.note || "No notes provided."}
                  </Typography>
                </Box>
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  sx={{ mt: 2 }}
                >
                  Options:
                </Typography>
                {selectedConfiguration.optionNames &&
                selectedConfiguration.optionNames.length > 0 ? (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {selectedConfiguration.optionNames.map(
                      (option: string, index: number) => (
                        <Chip key={index} label={option} />
                      )
                    )}
                  </Box>
                ) : (
                  <Typography variant="body1" color="text.secondary">
                    No options selected.
                  </Typography>
                )}
              </Box>
            ) : (
              <Typography variant="body1" color="text.secondary">
                No configuration details available.
              </Typography>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 3, borderTop: "1px solid #eaeaea" }}>
            <Button onClick={() => setIsConfigurationDialogOpen(false)}>
              Close
            </Button>
          </DialogActions>
        </Dialog>
        {isMobile && (
          <Fab
            color="primary"
            aria-label="add"
            onClick={() => setIsFormDialogOpen(true)}
            sx={{
              position: "fixed",
              bottom: 16,
              right: 16,
              bgcolor: "#C4B08C",
              "&:hover": { bgcolor: "#b39d77" },
            }}
          >
            <Add />
          </Fab>
        )}
      </Container>
    </LocalizationProvider>
  );
};

export default WishlistManagementPage;
